<script setup lang="ts">
import NbaPlayoffsImage from "@/assets/images/nba-playoffs/nba_playoffs_1x.webp";
import { useModalManager } from "@/composables/useModalManager";
import { usePayments } from "@/composables/usePayments";
import { useAuthenticationStore } from "@/stores/useAuthenticationStore";
import { ModalEnum } from "@/types/enums";
import BlackCheckIcon from "@/ui/components/icons/BlackCheckIcon.vue";
import { ref, watch } from "vue";
import BaseModal from "./BaseModal.vue";

const { activeModal, setActiveModal, setRedirectUrl } = useModalManager();
const { makePayment } = usePayments();
const authStore = useAuthenticationStore();
const show = ref(false);
const loading = ref(false);
const campaignName = import.meta.env.VITE_CAMPAIGN_NAME;

watch(
  () => activeModal.value,
  (modal) => {
    const campaign = localStorage.getItem("4c_campaign");
    const isDirectPayment = localStorage.getItem("4c_direct_payment");

    // 1. Is this a “new” campaign?
    const isNewCampaign = !campaign || campaign !== campaignName;
    // 2. Is the CampaignModal open?
    const isCampaignModalOpen = modal === ModalEnum.CampaignModal;
    // 3. Does the user lack access?
    const noUserOrNoAccess = !authStore.user || !authStore.user.has_access;
    // If ANY of our requirements fail, don’t show:
    if (!isNewCampaign) {
      show.value = false;
      return;
    }
    if (!isCampaignModalOpen) {
      show.value = false;
      return;
    }
    if (!noUserOrNoAccess) {
      show.value = false;
      return;
    }

    if (isDirectPayment) {
      show.value = false;
      return;
    }

    // All checks passed:
    localStorage.setItem("4c_campaign", campaignName);
    show.value = true;
  },
  { immediate: true }
);

const doPayment = () => {
  if (authStore.user && !authStore.user.has_access) {
    loading.value = true;
    makePayment(authStore.user, window.location.origin + "/models");
  } else {
    setRedirectUrl("/models");
    setActiveModal(ModalEnum.AuthModal, ModalEnum.PayAfterAuthModal);
  }
};

const onClose = () => {
  show.value = false;
  activeModal.value = null;
};
</script>

<template>
  <BaseModal @close="onClose" width="540px" v-model:visible="show">
    <template #header>
      <div class="flex w-full flex-col items-center justify-center self-stretch">
        <img class="h-[60px]" :src="NbaPlayoffsImage" />
      </div>
    </template>
    <div
      class="inline-flex w-full flex-col items-start justify-start gap-4 self-stretch overflow-x-hidden px-2 lg:gap-6"
    >
      <div class="flex flex-col items-start justify-start gap-1 self-stretch">
        <div
          class="justify-start self-stretch text-2xl font-semibold leading-[31.20px] text-slate-950"
        >
          NBA Playoffs Special - Limited Offer!
        </div>
        <div class="justify-start self-stretch text-base font-normal leading-normal text-slate-950">
          Get EVERY sports prediction on 4C Predictions.
        </div>
      </div>
      <div class="flex flex-col items-start justify-center gap-1 self-stretch">
        <div class="inline-flex items-center justify-start gap-1">
          <div
            class="justify-start text-[30px] font-semibold leading-10 text-slate-950 lg:text-[40px]"
          >
            $19
          </div>
          <div class="justify-end text-sm font-normal leading-none text-slate-950">/ month</div>
        </div>
        <div class="inline-flex items-center justify-start gap-2">
          <div class="justify-start text-sm font-normal leading-[21px] text-slate-950 line-through">
            $199 / month
          </div>
          <div
            class="flex items-center justify-center gap-2 rounded-[100px] bg-slate-950 px-4 py-1"
          >
            <div class="justify-start text-xs font-normal leading-[18px] text-white">
              For the first 777 Customers.
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-start justify-start gap-2">
        <div class="inline-flex h-6 max-w-[491px] items-start justify-start gap-1">
          <div class="flex items-center justify-center gap-2.5 p-1">
            <div class="relative h-4 w-4">
              <BlackCheckIcon class="absolute left-0 top-[2.29px] h-[11.43px] w-4" />
            </div>
          </div>
          <div class="flex-1 justify-start">
            <span class="text-base leading-normal text-slate-950"
            ><b>All Sports Predictions.</b></span
            >
          </div>
        </div>
        <div class="inline-flex h-6 max-w-[491px] items-start justify-start gap-1">
          <div class="flex items-center justify-center gap-2.5 p-1">
            <div class="relative h-4 w-4 overflow-hidden">
              <BlackCheckIcon class="absolute left-0 top-[2.29px] h-[11.43px] w-4" />
            </div>
          </div>
          <div class="flex-1 justify-start">
            <span class="text-base font-semibold leading-normal text-slate-950">78% </span>
            <span class="text-base font-normal leading-normal text-slate-950">Accurate </span>
            <span class="text-base font-semibold leading-normal text-slate-950">AI Models</span>
            <span class="text-base font-normal leading-normal text-slate-950">.</span>
          </div>
        </div>
        <div class="inline-flex h-6 max-w-[491px] items-start justify-start gap-1">
          <div class="flex items-center justify-center gap-2.5 p-1">
            <div class="relative h-4 w-4">
              <BlackCheckIcon class="absolute left-0 top-[2.29px] h-[11.43px] w-4" />
            </div>
          </div>
          <div class="flex-1 justify-start">
            <span class="text-base font-semibold leading-normal text-slate-950">Instant Picks</span
            ><span class="text-base font-normal leading-normal text-slate-950">, No Delays.</span>
          </div>
        </div>
        <div class="inline-flex h-6 max-w-[491px] items-start justify-start gap-1">
          <div class="flex items-center justify-center gap-2.5 p-1">
            <div class="relative h-4 w-4">
              <BlackCheckIcon class="absolute left-0 top-[2.29px] h-[11.43px] w-4" />
            </div>
          </div>
          <div class="flex-1 justify-start">
            <span class="text-base font-normal leading-normal text-slate-950">Get the edge </span
            ><span class="text-base font-semibold leading-normal text-slate-950"
          >sports books fear.</span
          >
          </div>
        </div>
        <div class="inline-flex h-6 max-w-[491px] items-start justify-start gap-1">
          <div class="flex items-center justify-center gap-2.5 p-1">
            <div class="relative h-4 w-4">
              <BlackCheckIcon class="absolute left-0 top-[2.29px] h-[11.43px] w-4" />
            </div>
          </div>
          <div class="flex-1 justify-start">
            <span class="text-base font-semibold leading-normal text-slate-950">Sharp bettors </span
            ><span class="text-base font-normal leading-normal text-slate-950">use AI.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-start justify-start gap-4 self-stretch px-6 lg:p-6">
      <Button :disabled="loading" @click="doPayment" class="mt-3 w-full px-[11px] py-[11px] shadow">
        <i v-if="loading" class="pi pi-spin pi-spinner"></i>
        <div class="text-xl font-bold">Start Winning Now!</div>
      </Button>
      <div
        class="justify-start self-stretch text-center text-xs font-normal leading-[18px] text-slate-950"
      >
        Billed Monthly | Cancel Anytime
      </div>
    </div>
  </BaseModal>
</template>
